import Cookies from 'js-cookie';
import { Controller } from '@hotwired/stimulus';
import events from 'lib/events';
import {
  getFormattedPageValue, triggerCustomEvent, splitTestEnabled,
} from 'lib/helpers';
import SearchStateManager from 'lib/search-state';

declare global {
  interface Window {
    EezyScrollPositionHelper:any
  }
}

export default class SearchController extends Controller {
  declare contentType:string;

  declare searchFormInput:HTMLInputElement;

  searchState:SearchStateManager;

  connect() {
    this.searchState = new SearchStateManager();

    this.searchFormInput = document.querySelector('[data-search-form-target="input"]');

    if (this.searchFormInput) {
      this.contentType = this.searchFormInput.getAttribute('data-content-type');

      // hacky support for png in pagination
      if (window.location.href.indexOf('/free-png/') > -1) this.contentType = 'png';
    }

    window.addEventListener('PostSearchAjax', this.postAjaxEvent);
    window.addEventListener('SearchPaginationChange', this.searchPaginationEvent);
    window.addEventListener('Searched', this.performSearch);
    window.addEventListener('click', this.trackSearchClick);
    window.addEventListener('click', this.trackSponsoredSearchClick);
    window.addEventListener(events.search.apply_filters, this.applySearchFilters);
    window.addEventListener(events.search.update_content_type, this.resetContentType);
  }

  disconnect() {
    window.removeEventListener('PostSearchAjax', this.postAjaxEvent);
    window.removeEventListener('SearchPaginationChange', this.searchPaginationEvent);
    window.removeEventListener('Searched', this.performSearch);
    window.removeEventListener('click', this.trackSearchClick);
    window.removeEventListener('click', this.trackSponsoredSearchClick);
    window.removeEventListener(events.search.apply_filters, this.applySearchFilters);
    window.removeEventListener(events.search.update_content_type, this.resetContentType);
  }

  applySearchFilters = (event:Event) => {
    this.resetContentType(event);

    Cookies.set('ezSearchType', 'internal');

    window.EezyScrollPositionHelper.preservePositionOnce();

    const queryString:string = this.searchState.getFlatQueryString();

    const searchUrl:string = this.unverifiedPath || this.changingContentType()
      ? this.getNewSearchUrl(this.searchRoute, this.searchTerm, queryString)
      : `${window.location.pathname}${queryString}`;

    Turbolinks.visit(searchUrl);
  }

  postAjaxEvent = (event) => {
    this.postAjax(event.detail.dataHash);
  }

  searchPaginationEvent = (event) => {
    triggerCustomEvent('PaginationChanged', {
      page: event.detail.page,
      stateKey: 'search-state',
      extraStates: ['filterState'],
      ajaxEventName: 'PostSearchAjax',
      resultsTarget: document.querySelector('.search-results'),
    });
  }

  performSearch = (event) => {
    const { searchPath } = event.detail;
    const searchRoute:string = event.detail.searchRoute ? event.detail.searchRoute : this.searchRoute;
    const term:string = this.normalizeTerm(event.detail.term);

    this.resetContentType(event);

    const queryString:string = event.detail.useSearchState ? this.searchState.getFlatQueryString().substring(1) : null;
    const newSearchUrl:string = searchPath || this.getNewSearchUrl(searchRoute, term, queryString);

    if (event.detail.searchInNewTab === true) {
      window.open(newSearchUrl, '_blank');
    } else {
      Turbolinks.visit(newSearchUrl);
    }
  }

  resetContentType = (event) => {
    this.contentType = event.detail.contentType || this.contentType;

    if (event.detail.contentType) Cookies.set('content_type', event.detail.contentType);

    if (this.changingContentType()) {
      this.searchState.resetStateToDefault();
      if (!splitTestEnabled('remove_apply_filters_mobile')) triggerCustomEvent('search:reset_hidden_filters');
    }
  }

  changingContentType() {
    return this.contentType !== this.searchFormInput.getAttribute('data-content-type');
  }

  toggleSearchFilters() {
    const toggleSearchFiltersEvent = new CustomEvent('search:toggle_search_filters');
    window.dispatchEvent(toggleSearchFiltersEvent);
  }

  postAjax = (dataHash) => {
    this.searchState.updateStateValue('page', dataHash.page);

    const queryParams:string = this.searchState.getFlatQueryString();
    const newSearchUrl:string = this.getNewSearchUrl(this.searchRoute, this.searchTerm, queryParams);

    if (this.searchType) Cookies.set('ezSearchType', this.searchType);

    window.location.assign(newSearchUrl);
  };

  getNewSearchUrl(searchRoute, term, queryString) {
    const url:string = searchRoute;
    const newQueryParams:string[] = [];

    if (searchRoute === this.searchRoute) {
      newQueryParams.push(`qterm=${term}`, `content_type=${this.contentType}`);
    }

    if (queryString) {
      queryString.replace(/^\?/, '').split('&').forEach((param) => {
        newQueryParams.push(param);
      });
    }

    if (newQueryParams.length > 0) return `/${url}?${newQueryParams.join('&')}`;

    return url;
  }

  footerPagerChange(event:Event) {
    event.preventDefault(); // eslint-disable-line no-restricted-globals

    const curPageValue:HTMLElement = document.querySelector('[data-ez-num-pager-target="currentPage"]');
    const nextPageValue:number = getFormattedPageValue(curPageValue) + 1;

    triggerCustomEvent('SearchPaginationChange', { page: nextPageValue });
  }

  trackSearchClick = (event:Event) => {
    const gridItem:HTMLElement = (event.target as HTMLElement).closest('.search-results .ez-search-results .ez-resource-grid__item');
    if (gridItem) {
      triggerCustomEvent(events.search.click, {
        resourceId: gridItem.getAttribute('data-item-id'),
        resourcePosition: gridItem.getAttribute('data-position'),
        resourcePro: gridItem.getAttribute('data-pro'),
        searchTerm: this.searchTerm,
      });
    }
  }

  trackSponsoredSearchClick = (event:Event) => {
    const sponsoredGridItem:HTMLElement = (event.target as HTMLElement).closest('.search-results .ez-sponsored-results .ez-resource-grid__item');
    if (sponsoredGridItem) {
      triggerCustomEvent(events.search.sponsored_click, {
        resourceId: sponsoredGridItem.getAttribute('data-item-id'),
        resourcePosition: sponsoredGridItem.getAttribute('data-index'),
        resourcePro: '',
        searchTerm: this.searchTerm,
        sponsored: true,
      });
    }
  }

  normalizeTerm(term:string) {
    return encodeURIComponent(term.toLowerCase().trim().replace(/ /g, '-'));
  }

  get searchRoute() {
    if (this.searchFormInput) return this.searchFormInput.getAttribute('data-search-route');

    return 'search';
  }

  get searchTerm() {
    const searchInfo:HTMLElement = this.element.querySelector('[data-search-term]');

    if (searchInfo) return searchInfo.getAttribute('data-search-term');

    return '';
  }

  get searchType() {
    const searchInfo:HTMLElement = this.element.querySelector('[data-search-type]');

    if (searchInfo) return searchInfo.getAttribute('data-search-type');

    return null;
  }

  get unverifiedPath() {
    return window.location.pathname === `/${this.searchRoute}`;
  }
}
